<template>
  <div>
    <h3>{{$t('message.script_config_user_management') | title}}</h3>
    <b-button class="padmin-button" @click="showAddUser">
      {{$t('message.user_management_add_user') | title}}
    </b-button>
    <b-table striped hover :items="users" :fields="fields" :tbody-tr-class=rowClass>
      <template #cell(actions)="row">
        <div class="table-actions-cell">
          <svg v-if="row.item.multifactor_shared_secret_set" class='icon icon-del-secret' @click="deleteSharedSecret(row.item, row.index)">
            <title>{{$t("message.button_delete_secret")}}</title>
          </svg>
          <svg class='icon icon-edit' @click="showUpdateUser(row.item, row.index)">
            <title>{{$t("message.button_edit")}}</title>
          </svg>
          <svg v-if="row.item.disabled == false" class='icon icon-disable' @click="disableUser(row.item, row.index)">
            <title>{{$t("message.button_disable")}}</title>
          </svg>
          <svg v-if="row.item.disabled == true" class='icon icon-enable' @click="enableUser(row.item, row.index)">
            <title>{{$t("message.button_enable")}}</title>
          </svg>
          <svg class='icon icon-delete' @click="showDeleteUser(row.item, row.index)">
            <title>{{$t("message.button_delete")}}</title>
          </svg>
        </div>
      </template>
    </b-table>

    <b-modal id="edit-user-modal" ref="edit-user-modal" no-close-on-backdrop hide-header hide-footer
      size="lg">
      <b-container fluid>
        <b-form enctype="multipart/form-data" align-h="center">
          <b-form-row>
            <label class="label label-default col-3 offset-2 font-weight-bold">
              {{$t("message.user_management_username") | title}}*
            </label>
            <b-form-input v-model="editedUser.username" :required="true" autocomplete="new-username"/>
          </b-form-row>
          <b-form-row>
            <label class="label label-default col-3 offset-2">{{$t("message.user_management_first_name") | title}}</label>
            <b-form-input v-model="editedUser.first_name" :required="true" autocomplete="new-username"/>
          </b-form-row>
          <b-form-row>
            <label class="label label-default col-3 offset-2">{{$t("message.user_management_last_name") | title}}</label>
            <b-form-input v-model="editedUser.last_name" :required="true" autocomplete="new-username"/>
          </b-form-row>
          <b-form-row>
            <label class="label label-default col-3 offset-2 font-weight-bold">
              {{$t("message.user_management_email") | title}}*
            </label>
            <b-form-input v-model="editedUser.email" :required="true" autocomplete="off"/>
          </b-form-row>
          <b-form-row>
            <label class="label label-default col-3 offset-2">
              {{$t("message.user_management_multifactor_required") | title}}*
            </label>
            <div class="col-1">
              <div class="form-group form-check">
                <input type="checkbox" class="form-check-input" v-model="editedUser.multifactor_required">
              </div>
            </div>
          </b-form-row>
          <b-form-row>
            <label class="label label-default col-3 offset-2" :class="{'font-weight-bold': isEditedUserNew()}">
              {{$t("message.user_management_password") | title}}<span v-if="isEditedUserNew()">*</span>
            </label>
            <b-form-input :type="passwordInputType" v-model="editedUser.password"
              :placeholder="isEditedUserNew() ? '' : $t('message.no_change')" autocomplete="new-password"/>
            <div class="col-1" v-if="passwordInputType  == 'password'">
              <svg class='icon icon-eye' @click="passwordInputType='text'"/>
            </div>
            <div class="col-1" v-if="passwordInputType  == 'text'">
              <svg class='icon icon-eye-active' @click="passwordInputType='password'"/>
            </div>
          </b-form-row>
          <b-form-row>
            <label class="label label-default col-3 offset-2" :class="{'font-weight-bold': isEditedUserNew()}">
              {{$t("message.user_management_password_again") | title}}<span v-if="isEditedUserNew()">*</span>
            </label>
            <b-form-input :type="passwordAgainInputType" v-model="editedUser.password_again"
              :placeholder="isEditedUserNew() ? '' : $t('message.no_change')" autocomplete="new-password"/>
            <div class="col-1" v-if="passwordAgainInputType  == 'password'">
              <svg class='icon icon-eye' @click="passwordAgainInputType='text'"/>
            </div>
            <div class="col-1" v-if="passwordAgainInputType  == 'text'">
              <svg class='icon icon-eye-active' @click="passwordAgainInputType='password'"/>
            </div>
          </b-form-row>
          <b-form-row v-if="groupOptions && groupOptions.length > 0">
            <label class="label label-default col-3 offset-2">
              {{$t('message.user_management_roles') | title}}
            </label>
          </b-form-row>
          <b-form-row v-for="group in groupOptions" class="form-check">
            <b-col offset="6">
              <input class="form-check-input" type="checkbox" :value="group.value" :id="group.value"
                v-model="editedUser.groups" @change="loadNav()"/>
              <label class="form-check-label" :for="group.value">{{group.text}}</label>
            </b-col>
          </b-form-row>
          <b-form-row>
            <b-col offset="5">
              <b-button v-if="groupOptions && groupOptions.length > 0"
                v-b-toggle.accessible-menus-collapse variant="secondary">
                {{$t('message.user_management_accessible_menus') | title}}
              </b-button>
            </b-col>
          </b-form-row>
          <b-collapse id="accessible-menus-collapse">
            <div v-if="nav" id="user-man-groups-nav">
              <div v-for="navElem in nav" :text="$t('message.script_' + navElem.menu)"
                :key="navElem.menu">
                <span class="font-weight-bold">{{navElem.menu}}:</span>
                <span v-for="(scriptName, scriptIndex) in navElem.scripts"
                  :key="scriptIndex">
                  {{ $t('message.script_' + navElem.menu + '_' + scriptName) + ((scriptIndex != navElem.scripts.length - 1) ? ',' : '') }}
                </span>
              </div>
            </div>
          </b-collapse>
          <b-alert v-model="showError" variant="danger" dismissible @dismissed="error=''; showError=false;">
            <div> {{ error }} </div>
          </b-alert>
        </b-form>
        <b-modal-footer>
          <b-button variant="secondary" @click="$refs['edit-user-modal'].hide()">
            {{$t('message.button_close') | title}}
          </b-button>
          <b-button class="submit-button" @click="saveUser" :disabled="!saveEnabled">
            <b-spinner v-if="saveInProgress" small type="grow"/>
            {{$t('message.button_save') | title}}
          </b-button>
        </b-modal-footer>
      </b-container>
    </b-modal>

    <b-modal ref="delete-confirm-modal" size="sm" @ok="deleteUser()"
      ok-only :title="$t('message.confirmation_title')" no-close-on-backdrop>
    {{$t('message.confirmation_text')}}
    </b-modal>
  </div>
</template>

<script>
import { get, post, del, patch } from "../http"
import { timestampToDatetime, validateEmail, validatePassword, validateUsername } from "../util"

export default {
  name: "UserManagement",

  data() {
    return {
      users: [],
      fields: [
        {key: 'username', sortable: true}, {key: 'first_name', sortable: true},
        {key: 'last_name', sortable: true}, {key: 'email', sortable: true},
        {key: 'last_password_change', sortable: true}, {key: 'multifactor_required', sortable: true},
        'actions'],
      editedUser: {},
      showError: false,
      error: '',
      passwordInputType: 'password',
      passwordAgainInputType: 'password',
      groupOptions: [],
      nav: null,
      saveInProgress: false
    }
  },

  mounted() {
    this.load()
  },

  computed: {
    saveEnabled() {
      if (this.saveInProgress) {
        return false
      }
      if (!this.editedUser.username  || !this.editedUser.email) {
        return false
      }
      if(this.isEditedUserNew()) {
        // add
        if (!this.editedUser.password || this.editedUser.password.length == 0) {
          return false
        }
      }
      if (this.editedUser.password && this.editedUser.password.length > 0) {
        if (this.editedUser.password != this.editedUser.password_again) {
          return false
        }
      }
      return true
    }
  },

  methods: {
    load() {
      get('/users').then(
        resp => {
          this.users = resp.data
          for (let iUser = 0; iUser < this.users.length; iUser++) {
            let user = this.users[iUser]
            user.last_password_change = timestampToDatetime(user.last_password_change)
          }
        },
        err => {
          console.log('err', err);
        }
      )

      get('/user_man_groups').then(
        resp => {
          for(let groupVal of resp.data) {
            let groupTextWords = groupVal.substring(4).replace('_', ' ').split(' ')
            let groupText = groupTextWords.map(word => {
              return word[0].toUpperCase() + word.substring(1)
            }).join(' ')
            this.groupOptions.push({value: groupVal, text: groupText})
          }
        },
        err => {
          console.log('err', err);
        }
      )
    },

    saveUser() {
      this.error = ''
      if (this.editedUser.password !== this.editedUser.password_again) {
        this.error = this.$t('message.passwords_dont_match')
        this.showError = true
        return
      }

      let data = {}
      let fields = ['username', 'first_name', 'last_name', 'email', 'password', 'groups',
                  'multifactor_required']
      for (let iField = 0; iField < fields.length; iField++) {
        let field = fields[iField]
        let value = this.editedUser[field]
        if (field == 'username' && (!value || value.length == 0)) {
          this.error = this.$t('message.error_invalid_username')
          this.showError = true
          return
        }
        if (field == 'email' && !validateEmail(value)) {
          this.error = this.$t('message.error_invalid_email')
          this.showError = true
          return
        }
        if (field == 'password') {
          if (!value) continue
          let weakPasswdError = validatePassword(value)
          if (weakPasswdError) {
            this.error = this.$t('message.error_password_weak') + ': '
              + this.$t('message.error_password_' + weakPasswdError) + '!'
            this.showError = true
            return
          }
        }
        if (field == 'username' && !validateUsername(value)) {
          this.error = this.$t('message.error_invalid_username')
          this.showError = true
          return
        }

          data[field] = value
      }

      if (this.isEditedUserNew()) {
        this.addUser(data)
      } else {
        this.updateUser(this.editedUser.user_id, this.editedUser.index, data)
      }
    },

    addUser(data) {
      this.saveEnabled = false
      post('/users', data, {'Content-Type': 'application/json'}).then(
        resp => {
          this.saveEnabled = true
          if (resp.data.error) {
            this.error = resp.data.error
            this.showError = true
            return
          }
          let user = resp.data.user
          user.last_password_change = timestampToDatetime(user.last_password_change)
          this.users.push(user)
          this.$refs['edit-user-modal'].hide()
        },
        err => {
          this.saveEnabled = true
          console.log(err)
        }
      )
    },

    updateUser(user_id, index, data) {
      this.saveEnabled = false
      patch('/users?user_id=' + user_id, data, {'Content-Type': 'application/json'}).then(
        resp => {
          this.saveEnabled = true
          if (resp.data.error) {
            this.error = resp.data.error
            this.showError = true
            return
          }
          let user = resp.data.user
          user.last_password_change = timestampToDatetime(user.last_password_change)
          this.$set(this.users, index, user)
          this.$refs['edit-user-modal'].hide()
        },
        err => {
          this.saveEnabled = true
          console.log(err)
        }
      )
    },

    disableUser(user, index) {
      let data = {'disabled': true}
      this.updateUser(user.user_id, index, data)
    },

    enableUser(user, index) {
      let data = {'disabled': false}
      this.updateUser(user.user_id, index, data)
    },

    deleteSharedSecret(user, index) {
      let data = {'delete_shared_secret': true}
      this.updateUser(user.user_id, index, data)
    },

    deleteUser() {
      del('/users?user_id=' + this.editedUser.user_id).then(
        resp => {
          this.users.splice(this.editedUser.index, 1)
        },
        err => {
          console.log('err', err)
        }
      )
    },

    showAddUser() {
      this.showError = false
      this.editedUser={groups: []}
      this.$refs['edit-user-modal'].show()
    },

    showUpdateUser(user, index) {
      this.editedUser = JSON.parse(JSON.stringify(user))
      this.editedUser.index = index
      this.loadNav()
      this.$refs['edit-user-modal'].show()
    },

    showDeleteUser(user, index) {
      this.editedUser = {user_id: user.user_id, index: index}
      this.$refs['delete-confirm-modal'].show()
    },

    rowClass(item, type) {
      if (!item || type !== 'row') return
      if (item.disabled == true) return 'disabled'
    },

    isEditedUserNew() {
      return typeof this.editedUser.user_id === 'undefined'
    },

    loadNav() {
      this.nav = null
      post('/scripts_nav_groups', {'groups': this.editedUser.groups}, {'Content-Type': 'application/json'}).then(
        resp => {
          this.nav = resp.data;
        },
        err => {
          console.log("err", err);
        }
      )
    }
  }
}
</script>
